import { useCallback, useMemo } from 'react';

import { useApi } from '@/shared/api';
import { Permission } from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { useAuthStore } from '@/shared/store/auth.store';

import { useIwEditingStore } from '../stores';

import { useInterwillsActions } from './useInterwillActions';

const ALLOWED_ROLES = ['WITNESS', 'EXECUTOR'];

export const useParticipantActions = () => {
  const { witnesses, executor, beneficiaries } = useIwEditingStore();
  const { account } = useAuthStore();
  const actions = useInterwillsActions();
  const sjDb = useSJDatabase();
  const api = useApi();

  const isExecutor = Boolean(executor && account && executor.email === account.email);

  const isBeneficiary = beneficiaries.some((item) => item.email === account?.email);

  const getWitnessIndexById = useCallback(
    (id: string) => witnesses.findIndex((item) => item.id === id),
    [witnesses],
  );

  const getCurrentWitnessIndex = useCallback(
    () =>
      witnesses.findIndex((item) =>
        Boolean(item.email && account?.email && item.email.toLowerCase() === account.email),
      ),
    [witnesses, account?.email],
  );

  const getCurrentWitness = useCallback(() => {
    const witnessIndex = getCurrentWitnessIndex();
    return witnessIndex >= 0 ? witnesses[witnessIndex] : null;
  }, [getCurrentWitnessIndex, witnesses]);

  const getCurrentBeneficiary = useCallback(() => {
    return beneficiaries.find((item) => item.email === account?.email);
  }, [beneficiaries]);

  const updateParticipantsPermissions = async (action: 'put' | 'delete') => {
    const will = await actions.getInterwill();
    if (!will?.centralDbInterwillId) return;

    const { data: allParticipants } = await api.willsGetParticipants(will.centralDbInterwillId);
    const allBeneficiaries = await api.profilesGetBeneficiaries();

    const participants = allBeneficiaries.data?.filter(
      (item) =>
        allParticipants?.some((participant) => {
          const isRoleAllowed = ALLOWED_ROLES.includes(participant.role);
          return participant.profileId === item.id && isRoleAllowed;
        }),
    );
    const interwillsCollection = sjDb.collections.interwills;

    participants?.forEach((participant) => {
      if (!participant.publicKey) return;

      if (action === 'put') {
        interwillsCollection?.putPublicKey(will.id, participant.publicKey);
        interwillsCollection?.putPermissions(will.id, participant.publicKey, [
          Permission.READ,
          Permission.WRITE,
        ]);
      }

      if (action === 'delete') {
        interwillsCollection?.deletePublicKey(will.id, participant.publicKey);
        interwillsCollection?.deletePermission(will.id, participant.publicKey, Permission.READ);
        interwillsCollection?.deletePermission(will.id, participant.publicKey, Permission.WRITE);
      }
    });
  };

  const getParticipants = async () => {
    const resWill = await actions.getInterwill();
    if (!resWill.centralDbInterwillId) return null;

    const { data: allParticipants } = await api.willsGetParticipants(resWill.centralDbInterwillId);
    const allBeneficiaries = await api.profilesGetBeneficiaries();

    const participants = allBeneficiaries.data?.filter(
      (item) => allParticipants?.some((participant) => participant.profileId === item.id),
    );

    return participants;
  };

  return useMemo(
    () => ({
      isExecutor,
      isBeneficiary,
      getWitnessIndexById,
      getCurrentWitnessIndex,
      getCurrentWitness,
      updateParticipantsPermissions,
      getCurrentBeneficiary,
      getParticipants,
    }),
    [
      isExecutor,
      isBeneficiary,
      getWitnessIndexById,
      getCurrentWitnessIndex,
      getCurrentWitness,
      updateParticipantsPermissions,
      getCurrentBeneficiary,
      getParticipants,
    ],
  );
};
